// [PURPOSE]: Batch 6 / P0 C-5 — XGtE p.133 spell-scroll scribing rules.
// [WHY]: Pre-fix, ScrollScribeDialog and App.jsx scroll-creation used the
//   formula `Math.max(25, level² × 25)` gp + `Math.max(2, level² × 2)` hours,
//   which diverged from RAW by 1–100× and was undocumented homebrew. Per
//   feedback_no_homebrew_without_request.md, RAW is the only acceptable
//   default — homebrew variants must be opt-in.
// [DESIGN]: A flat lookup table keyed by spell level (0=cantrip through 9).
//   Each row carries `gp` (material + tools cost), `days` (downtime
//   required), and `hours` (days × 8 — convenience for callers that
//   track hours rather than days, e.g. downtimeHours on playerStats).
// [INTEGRATION]: Consumed by ScrollScribeDialog.js (cost display) and
//   App.jsx handleScrollScribe (currency deduction + downtime advance).
// [REFERENCE]: Xanathar's Guide to Everything p.133, "Scribing a Spell Scroll"
//   table. Times listed as "1 workweek" / "1 day" / etc. — converted to
//   absolute days here for easier consumer math. RAW work-day = 8 hours.
//
// DMG p.200 lists scroll MARKET values by rarity (the price the scroll
// commands when sold, distinct from the scribing cost):
//   Common (cantrip + 1st)   →  50 gp
//   Uncommon (2nd + 3rd)     →  250 gp
//   Rare (4th + 5th)         →  2,500 gp
//   Very Rare (6th–8th)      →  25,000 gp
//   Legendary (9th)          →  250,000 gp
// Stored on each row as `marketGp` so InventoryItem.value reflects the
// scroll's worth, not what the player paid to scribe it (which is often
// LOWER for low-level scrolls — scribing a 1st-level costs 25gp but the
// scroll sells for ~50gp).

export const XGTE_SCROLL_TABLE = Object.freeze({
  0: Object.freeze({ gp: 15,      days: 1,   hours: 8,   marketGp: 50      }),
  1: Object.freeze({ gp: 25,      days: 1,   hours: 8,   marketGp: 50      }),
  2: Object.freeze({ gp: 250,     days: 3,   hours: 24,  marketGp: 250     }),
  3: Object.freeze({ gp: 500,     days: 5,   hours: 40,  marketGp: 250     }),
  4: Object.freeze({ gp: 2500,    days: 10,  hours: 80,  marketGp: 2500    }),
  5: Object.freeze({ gp: 5000,    days: 25,  hours: 200, marketGp: 2500    }),
  6: Object.freeze({ gp: 15000,   days: 40,  hours: 320, marketGp: 25000   }),
  7: Object.freeze({ gp: 25000,   days: 50,  hours: 400, marketGp: 25000   }),
  8: Object.freeze({ gp: 50000,   days: 60,  hours: 480, marketGp: 25000   }),
  9: Object.freeze({ gp: 250000,  days: 120, hours: 960, marketGp: 250000  }),
});

/**
 * Look up the XGtE scroll-scribing cost for a given spell level.
 * Defensive fallbacks: invalid / out-of-range levels clamp to the
 * nearest valid row rather than throwing — production callers may
 * receive stray data from custom-spell creators.
 *
 * @param {number} level - Spell level 0 (cantrip) through 9.
 * @returns {{gp: number, days: number, hours: number}}
 */
export function getScrollScribeCost(level) {
  const n = Number.isFinite(level) ? Math.floor(level) : 0;
  const clamped = Math.max(0, Math.min(9, n));
  return XGTE_SCROLL_TABLE[clamped];
}

export default { XGTE_SCROLL_TABLE, getScrollScribeCost };
